import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { AppLayout } from "@/components/layout/AppLayout";
import Index from "./pages/Index";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import SupplierResponse from "./pages/SupplierResponse";
import UserManagementPage from "./pages/UserManagementPage";
import DisplaySettings from "./pages/DisplaySettings";
import NotificationsSettings from "./pages/NotificationsSettings";
import SupplierPortal from "./pages/SupplierPortal";
import AdminPage from "./pages/AdminPage";
import AcceptInvite from "./pages/AcceptInvite";
import PARSystemsPage from "./pages/PARSystemsPage";
import BacktestPage from "./pages/BacktestPage";
import BacktestNewPage from "./pages/BacktestNewPage";
import BacktestResultsPage from "./pages/BacktestResultsPage";
import DemoPage from "./pages/DemoPage";
import ResetPassword from "./pages/ResetPassword";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import ScratchpadPage from "@/pages/ScratchpadPage";
import OntologyPage from "@/pages/OntologyPage";
import DataSourcesPage from "@/pages/DataSourcesPage";
import TransformsPage from "@/pages/TransformsPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: false,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}

// Redirects PAR-role users to /par-systems; all other authenticated users pass through.
function NonPARRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const { isPAR, isLoading: isRoleLoading } = useRole();

  if (isLoading || isRoleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (isPAR) {
    return <Navigate to="/par-systems" replace />;
  }

  return <>{children}</>;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/"
                element={
                  <NonPARRoute>
                    <Index />
                  </NonPARRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <NonPARRoute>
                    <UserManagementPage />
                  </NonPARRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <NonPARRoute>
                    <DisplaySettings />
                  </NonPARRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <NonPARRoute>
                    <NotificationsSettings />
                  </NonPARRoute>
                }
              />
              <Route path="/offer/:invoiceId" element={<SupplierResponse />} />
              <Route path="/supplier-response" element={<SupplierResponse />} />
              <Route path="/supplier" element={<SupplierPortal />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route
                path="/par-systems"
                element={
                  <ProtectedRoute>
                    <PARSystemsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/backtest"
                element={
                  <NonPARRoute>
                    <BacktestPage />
                  </NonPARRoute>
                }
              />
              <Route
                path="/backtest/new"
                element={
                  <NonPARRoute>
                    <BacktestNewPage />
                  </NonPARRoute>
                }
              />
              <Route
                path="/backtest/:runId"
                element={
                  <NonPARRoute>
                    <BacktestResultsPage />
                  </NonPARRoute>
                }
              />
              <Route path="/demo" element={<DemoPage />} />
              <Route
                path="/scratchpad"
                element={
                  <ProtectedRoute>
                    <ScratchpadPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account-settings"
                element={
                  <ProtectedRoute>
                    <AccountSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ontology"
                element={
                  <ProtectedRoute>
                    <OntologyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/data-sources"
                element={
                  <ProtectedRoute>
                    <DataSourcesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transforms"
                element={
                  <ProtectedRoute>
                    <TransformsPage />
                  </ProtectedRoute>
                }
              />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
